import {
  toISO8601String,
  handleHTTPResponse,
  appendParamsToURL,
} from "./utils";
import { appConfig } from "appConfig";
import { authHeaders, refreshSession, SESSION_END_HEADER } from "./oidc";

const { v2: apiV2Config } = appConfig.api;

/**
 * A 401 does not have a single meaning. The SPA holds a token that expires
 * every few minutes, so a 401 usually means "that token just aged out" — which
 * is recoverable and must not log anyone out.
 *
 * The backend tells the two apart: it sets X-Session-End-Reason only when the
 * session policy rejected the request. Absent that header, renewing and
 * retrying once is the right move.
 */
const isRecoverable401 = (response) =>
  response.status === 401 && !response.headers.get(SESSION_END_HEADER);

const callApi = async function (
  endPoint,
  payload = {},
  method = "POST",
  headers = {},
  params = null
) {
  let requestOptions = {
    method: method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...headers,
    },
  };

  // Body
  if (method === "POST" || method === "PATCH") {
    requestOptions.body = JSON.stringify(payload);
  }

  // Auth — the gatekeeper used to inject this upstream; now the SPA owns it.
  Object.assign(requestOptions.headers, await authHeaders());

  // URL
  const apiUrl = apiV2Config.baseUrl;
  const url = new URL(`${apiUrl}${endPoint}`);
  if (params) appendParamsToURL(url, params);

  // Response
  let response = await fetch(url, requestOptions);

  // Retry once, and only once, behind a shared renewal. A second 401 after a
  // successful renew is a real authorization failure, so it falls through to
  // handleHTTPResponse and ends the session.
  if (isRecoverable401(response) && (await refreshSession())) {
    Object.assign(requestOptions.headers, await authHeaders());
    response = await fetch(url, requestOptions);
  }

  return handleHTTPResponse(response);
};

export const apiV2 = {
  briefings: {
    /**
     * Creates a briefing.
     *
     * @param {Object} createBriefingOptions - The options for creating the briefing.
     * @param {string} createBriefingOptions.notes - The notes of the briefing.
     * @returns {Promise} - A promise that resolves with the result of the API call.
     */
    create: async (createBriefingOptions) => {
      return callApi(
        apiV2Config.endpoints.briefings,
        createBriefingOptions,
        "POST",
        {}
      );
    },
    /**
     * Retrieves all briefings from the API.
     *
     * @param {string} searchQuery - The search query to filter the briefings by content.
     * @returns {Promise} A promise that resolves to the response from the API.
     */
    getAll: async (filters) => {
      return callApi(
        apiV2Config.endpoints.briefings,
        {},
        "GET",
        {},
        filters
      );
    },
    /**
     * Retrieves a briefing by its ID.
     *
     * @param {number} id - The ID of the briefing to retrieve.
     * @returns {Promise} A promise that resolves to the retrieved briefing.
     */
    getById: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.briefings}/${id}`,
        {},
        "GET",
        {}
      );
    },

    /**
     * Updates a briefing with the provided options.
     *
     * @param {Object} updateBriefingOptions - The options for updating the briefing.
     * @param {number} id - The ID of the briefing to update.
     * @param {string} updateBriefingOptions.updateNotes - The updated notes for the briefing.
     * @returns {Promise} - A promise that resolves with the updated briefing.
     */
    update: async (id, updateBriefingOptions) => {
      return callApi(
        `${apiV2Config.endpoints.briefings}/${id}`,
        updateBriefingOptions,
        "PATCH",
        {}
      );
    },

    /**
     * Deletes a briefing with the specified ID.
     *
     * @param {number} id - The ID of the briefing to delete.
     * @returns {Promise} A Promise that resolves when the briefing is successfully deleted.
     */
    delete: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.briefings}/${id}`,
        {},
        "DELETE",
        {}
      );
    },
  },
  providers: {
    /**
     * Creates a provider.
     *
     * @param {Object} createProviderOptions - The options for creating the provider.
     * @returns {Promise} A promise that resolves with the result of the API call.
     */
    create: async (createProviderOptions) => {
      return callApi(
        apiV2Config.endpoints.providers,
        createProviderOptions,
        "POST",
        {}
      );
    },
    /**
     * Retrieves a provider by its ID.
     *
     * @param {number} id - The ID of the provider.
     * @returns {Promise} A promise that resolves to the provider data.
     */
    getById: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.providers}/${id}`,
        {},
        "GET",
        {}
      );
    },
    /**
     * Retrieves all providers
     *
     * @returns {Promise} A promise that resolves to a list with the providers data
     */
    getAll: async () => {
      return callApi(apiV2Config.endpoints.providers, {}, "GET", {});
    },

    /**
     * Updates a provider with the provided options.
     *
     * @param {Object} updateBriefingOptions - The options for updating the provider.
     * @param {number} id - The ID of the provider to update.
     * @returns {Promise} - A promise that resolves with the updated provider.
     */
    update: async (id, updateProviderOptions) => {
      return callApi(
        `${apiV2Config.endpoints.providers}/${id}`,
        updateProviderOptions,
        "PATCH",
        {}
      );
    },

    /**
     * Deletes a provider with the specified ID.
     *
     * @param {number} id - The ID of the provider to delete.
     * @returns {Promise} A Promise that resolves when the provider is successfully deleted.
     */
    delete: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.providers}/${id}`,
        {},
        "DELETE",
        {}
      );
    },
  },
  shiftAssignments: {
    /**
     * Assigns a shift to a Volunteer.
     *
     * @param {Object} createShiftAssignmentOptions - The options for creating the shift assignment.
     * @returns {Promise} - A promise that resolves with the result of the API call.
     */
    create: async (createShiftAssignmentOptions) => {
      return callApi(
        apiV2Config.endpoints.shiftAssignments,
        createShiftAssignmentOptions,
        "POST",
        {}
      );
    },

    /**
     * Retrieves all shift assignments within a date range.
     *
     * @param {Date|string|number} from - The start date for fetching shift assignments. Must be of the form YYYY-MM-DD.
     * @param {Date|string|number} to - The end date for fetching shift assignments. Must be of the form YYYY-MM-DD.
     * @returns {Promise} A promise that resolves to the response from the API.
     */
    getAll: async (filters) => {
      return callApi(
        apiV2Config.endpoints.shiftAssignments,
        {},
        "GET",
        {},
        filters
      );
    },

    /**
     * Deletes a shift-assignment with the specified ID.
     *
     * @param {number} id - The ID of the briefing to delete.
     * @returns {Promise} A Promise that resolves when the briefing is successfully deleted.
     */
    delete: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.shiftAssignments}/${id}`,
        {},
        "DELETE",
        {}
      );
    },
  },
  stats: {
    /**
     * retrieve the number of call entries grouped by month between two dates.
     * @param {Date|string|number} from - The start date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @param {Date|string|number} to - The end date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @returns {Promise<Object>} A promise that resolves with the API response data.
     */
    getEntriesPerMonth: async (from, to) => {
      const [fromDate, toDate] = [toISO8601String(from), toISO8601String(to)];
      return callApi(
        apiV2Config.endpoints.stats.entriesPerMonth,
        {},
        "GET",
        {},
        { from: fromDate, to: toDate }
      );
    },

    /**
     * retrieve the number of call entries grouped by state/province between two dates.
     * @param {Date|string|number} from - The start date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @param {Date|string|number} to - The end date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @returns {Promise<Object>} A promise that resolves with the API response data.
     */
    getEntriesByStateProvince: async (from, to) => {
      const [fromDate, toDate] = [toISO8601String(from), toISO8601String(to)];
      return callApi(
        apiV2Config.endpoints.stats.entriesByStateProvince,
        {},
        "GET",
        {},
        { from: fromDate, to: toDate }
      );
    },

    /**
     * retrieve the number of call entries grouped by call-taker between two dates.
     * @param {Date|string|number} from - The start date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @param {Date|string|number} to - The end date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @returns {Promise<Object>} A promise that resolves with the API response data.
     */
    getEntriesPerCallTaker: async (from, to) => {
      const [fromDate, toDate] = [toISO8601String(from), toISO8601String(to)];
      return callApi(
        apiV2Config.endpoints.stats.entriesPerCallTaker,
        {},
        "GET",
        {},
        { from: fromDate, to: toDate }
      );
    },

    /**
     * retrieve the number of shifts per call taker between two dates.
     * @param {Date|string|number} from - The start date for fetching shifts. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @param {Date|string|number} to - The end date for fetching shifts. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @returns {Promise<Object>} A promise that resolves with the API response data.
     */
    getShiftsPerCallTaker: async (from, to) => {
      const [fromDate, toDate] = [toISO8601String(from), toISO8601String(to)];
      return callApi(
        apiV2Config.endpoints.stats.shiftsPerCallTaker,
        {},
        "GET",
        {},
        { from: fromDate, to: toDate }
      );
    },

    /**
     * retrieve the number of calls that were referred to an specific organization or person netween two dates.
     * @param {Date|string|number} from - The start date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @param {Date|string|number} to - The end date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @returns {Promise<Object>} A promise that resolves with the API response data.
     */
    getReferralsPerMonth: async (from, to) => {
      const [fromDate, toDate] = [toISO8601String(from), toISO8601String(to)];
      return callApi(
        apiV2Config.endpoints.stats.referralsPerMonth,
        {},
        "GET",
        {},
        { from: fromDate, to: toDate }
      );
    },

    /**
     * retrieve the total number of call entries, including the counts of referred and non-referred entries.
     * @param {Date|string|number} from - The start date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @param {Date|string|number} to - The end date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @returns {Promise<Object>} A promise that resolves with the API response data.
     */
    getReferralsFrequency: async (from, to) => {
      const [fromDate, toDate] = [toISO8601String(from), toISO8601String(to)];
      return callApi(
        apiV2Config.endpoints.stats.referralsFrequency,
        {},
        "GET",
        {},
        { from: fromDate, to: toDate }
      );
    },

    /**
     * retrieve the number of call entries grouped by Copline Referral(How the caller heard about Copline.
     * @param {Date|string|number} from - The start date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @param {Date|string|number} to - The end date for fetching entries. Can be a Date object, a string, or a number that the Date constructor can handle.
     * @returns {Promise<Object>} A promise that resolves with the API response data.
     */
    getEntriesPerCoplineReferrals: async (from, to) => {
      const [fromDate, toDate] = [toISO8601String(from), toISO8601String(to)];
      return callApi(
        apiV2Config.endpoints.stats.entriesPerCoplineReferral,
        {},
        "GET",
        {},
        { from: fromDate, to: toDate }
      );
    },
  },
  textBlasts: {
    /**
     * Processes a text blast to be sent to all active users.
     * @param {Object} textBlastOptions - The options for the text blast.
     * @param {string} textBlastOptions.message - The message to be sent in the text blast.
     * @returns {Promise<void>} A promise that resolves when the text blast has been processed/enqueued to be sent.
     */
    send: async (textBlastOptions) => {
      return callApi(
        apiV2Config.endpoints.textBlasts,
        textBlastOptions,
        "POST",
        {}
      );
    },
  },
  honorWall: {
    recognitions: {
      /**
       * Retrieve all Honor Wall recognitions
       *
       * @returns {Promise<Object>} A promise that resolves to a list with the recognitions data
       */
      getAll: async () => {
        return callApi(
          apiV2Config.endpoints.honorWall.recognitions,
          {},
          "GET",
          {},
          true
        );
      },
    },
  },
  callTakers: {
    /**
     * Retrieves all active call takers from V2 API.
     *
     * @returns {Promise} Array of call taker data
     */
    getAll: async (filters) => {
      return callApi(apiV2Config.endpoints.callTakers, {}, "GET", {}, filters);
    },
    /**
     * Retrieves a call taker by their ID from V2 API.
     *
     * @param {string} id - The UUID of the call taker
     * @returns {Promise} Call taker data
     */
    getById: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.callTakers}/${id}`,
        {},
        "GET",
        {}
      );
    },
    /**
     * Creates a new call taker.
     *
     * @param {Object} createCallTakerOptions - The call taker data
     * @returns {Promise} Created call taker data
     */
    create: async (createCallTakerOptions) => {
      return callApi(
        apiV2Config.endpoints.callTakers,
        createCallTakerOptions,
        "POST",
        {}
      );
    },
    /**
     * Updates an existing call taker.
     *
     * @param {string} id - The UUID of the call taker to update
     * @param {Object} updateCallTakerOptions - The call taker data to update
     * @returns {Promise} Updated call taker data
     */
    update: async (id, updateCallTakerOptions) => {
      return callApi(
        `${apiV2Config.endpoints.callTakers}/${id}`,
        updateCallTakerOptions,
        "PATCH",
        {}
      );
    },
  },
  callReports: {
    /**
     * Creates a new call report.
     * If returnCall is true, also creates a linked return call record.
     *
     * @param {Object} createCallReportOptions - The call report data
     * @returns {Promise} Created call report data
     */
    create: async (createCallReportOptions) => {
      return callApi(
        apiV2Config.endpoints.callReports,
        createCallReportOptions,
        "POST",
        {}
      );
    },
    /**
     * Retrieves all call reports with filters from V2 API.
     *
     * @param {Object} filters - Filter options
     * @param {string} filters.type - Filter by type (ORIGIN or RETURN), defaults to ORIGIN
     * @param {string} filters.startDate - Start date for filtering (ISO 8601 format)
     * @param {string} filters.endDate - End date for filtering (ISO 8601 format)
     * @param {number} filters.limit - Limit number of call reports returned
     * @param {number} filters.offset - Offset for pagination
     * @returns {Promise} Paginated list of call reports with hasUnresolvedReturnCalls flag
     */
    getAll: async (filters) => {
      return callApi(
        apiV2Config.endpoints.callReports,
        {},
        "GET",
        {},
        filters
      );
    },
    /**
     * Retrieves a call report by its ID from V2 API.
     *
     * @param {number} id - The ID of the call report
     * @returns {Promise} Call report data with resolved and unresolved return calls
     */
    getById: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.callReports}/${id}`,
        {},
        "GET",
        {}
      );
    },
    /**
     * Updates a call report by its ID from V2 API.
     * If returnCall changes from false to true, creates a linked return call record.
     *
     * @param {number} id - The ID of the call report to update
     * @param {Object} updateCallReportOptions - The call report data to update
     * @returns {Promise} Updated call report data
     */
    update: async (id, updateCallReportOptions) => {
      return callApi(
        `${apiV2Config.endpoints.callReports}/${id}`,
        updateCallReportOptions,
        "PATCH",
        {}
      );
    },
    /**
     * Retrieves all return call reports from V2 API.
     *
     * @returns {Promise} Array of return call reports with hasUnresolvedReturnCalls flag
     */
    getReturnCalls: async () => {
      return callApi(
        `${apiV2Config.endpoints.callReports}/return-calls`,
        {},
        "GET",
        {}
      );
    },
    /**
     * Retrieves all return call reports for a specific origin call report from V2 API.
     *
     * @param {number} originId - The ID of the origin call report
     * @returns {Promise} Array of return call reports for the origin
     */
    getReturnCallsByOrigin: async (originId) => {
      return callApi(
        `${apiV2Config.endpoints.callReports}/${originId}/return-calls`,
        {},
        "GET",
        {}
      );
    },
    /**
     * Deletes a call report by its ID from V2 API.
     * Performs a soft delete (sets deleted = true).
     *
     * @param {number} id - The ID of the call report to delete
     * @returns {Promise} Empty response on success (204 No Content)
     */
    delete: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.callReports}/${id}`,
        {},
        "DELETE",
        {}
      );
    },
  },
  lessons: {
    /**
     * Creates a new lesson.
     *
     * @param {Object} lesson - The lesson data
     * @param {string} lesson.title - The lesson title
     * @param {string} lesson.videoUrl - URL to the lesson video
     * @param {Array} lesson.questions - Array of quiz questions
     * @param {string} [lesson.documentUrl] - URL to supporting document
     * @param {string} [lesson.description] - Lesson description
     * @returns {Promise} Created lesson data
     */
    create: async (lesson) => {
      return callApi(
        apiV2Config.endpoints.lessons,
        lesson,
        "POST",
        {}
      );
    },
    /**
     * Retrieves all non-deleted lessons with the call taker's responses.
     *
     * @param {string} callTakerId - The call taker ID
     * @returns {Promise} Array of lessons with responses and completed fields
     */
    getAll: async (callTakerId) => {
      return callApi(
        apiV2Config.endpoints.lessons,
        {},
        "GET",
        {},
        { callTakerId }
      );
    },
    /**
     * Retrieves a lesson by its ID from V2 API.
     *
     * @param {number} id - The ID of the lesson
     * @returns {Promise} Lesson data including questions
     */
    getById: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.lessons}/${id}`,
        {},
        "GET",
        {}
      );
    },
    /**
     * Updates an existing lesson by its ID.
     *
     * @param {number} id - The ID of the lesson to update
     * @param {Object} lesson - The lesson fields to update
     * @param {string} [lesson.title] - The lesson title
     * @param {string} [lesson.videoUrl] - URL to the lesson video
     * @param {Array} [lesson.questions] - Array of quiz questions
     * @param {string} [lesson.documentUrl] - URL to supporting document
     * @param {string} [lesson.description] - Lesson description
     * @returns {Promise} Updated lesson data
     */
    update: async (id, lesson) => {
      return callApi(
        `${apiV2Config.endpoints.lessons}/${id}`,
        lesson,
        "PATCH",
        {}
      );
    },
    /**
     * Deletes a lesson by its ID from V2 API.
     * Performs a soft delete (sets deleted = true).
     *
     * @param {number} id - The ID of the lesson to delete
     * @returns {Promise} Empty response on success (204 No Content)
     */
    delete: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.lessons}/${id}`,
        {},
        "DELETE",
        {}
      );
    },
    /**
     * Retrieves aggregated statistics for a lesson's quiz responses.
     *
     * @param {number} id - The ID of the lesson
     * @returns {Promise} Lesson statistics with answer distribution
     */
    getStats: async (id) => {
      return callApi(
        `${apiV2Config.endpoints.lessons}/${id}/stats`,
        {},
        "GET",
        {}
      );
    },

    /**
     * Downloads lesson responses as an Excel file.
     *
     * @param {number} id - The ID of the lesson
     * @returns {Promise<Blob>} Excel file blob
     */
    download: async (id) => {
      const url = `${apiV2Config.baseUrl}${apiV2Config.endpoints.lessons}/${id}/download`;
      const headers = await authHeaders();
      const response = await fetch(url, { headers });
      return response.blob();
    },
  },
  lessonResponses: {
    /**
     * Retrieves a call taker's response for a specific lesson.
     *
     * @param {number} lessonId - The ID of the lesson
     * @param {string} callTakerId - The ID of the call taker
     * @returns {Promise} Lesson response data or null
     */
    get: async (lessonId, callTakerId) => {
      return callApi(
        apiV2Config.endpoints.lessonResponses,
        {},
        "GET",
        {},
        { lessonId, callTakerId }
      );
    },

    /**
     * Retrieves all responses for a specific lesson.
     *
     * @param {number} lessonId - The ID of the lesson
     * @returns {Promise} Array of lesson response data
     */
    getAll: async (lessonId) => {
      return callApi(
        `${apiV2Config.endpoints.lessonResponses}/all`,
        {},
        "GET",
        {},
        { lessonId }
      );
    },

    /**
     * Creates a new lesson response or updates an existing one.
     * If the lessonResponse has an id, it updates; otherwise, it creates.
     *
     * @param {Object} lessonResponse - The lesson response data
     * @param {number} [lessonResponse.id] - The ID of an existing response (omit for create)
     * @param {Array} lessonResponse.responses - Array of selected answer indices
     * @param {number} lessonResponse.lesson - The lesson ID
     * @param {string} lessonResponse.callTakerId - The call taker ID
     * @returns {Promise} Created or updated lesson response data
     */
    save: async (lessonResponse) => {
      return callApi(
        apiV2Config.endpoints.lessonResponses,
        lessonResponse,
        "POST",
        {}
      );
    },

    /**
     * Marks a lesson response as completed or uncompleted.
     *
     * @param {number} id - The ID of the lesson response
     * @param {boolean} completed - Whether the lesson is completed
     * @returns {Promise} Updated lesson response data
     */
    complete: async (id, completed) => {
      return callApi(
        `${apiV2Config.endpoints.lessonResponses}/${id}/complete`,
        { completed },
        "POST",
        {}
      );
    },
  },
};
